/**
 * Helpful resources:
 * https://github.com/perawallet/connect
 */
import walletProviders from '../providers'
import type { PeraWalletConnect } from '@perawallet/connect'
import type { WalletProvider, Wallet } from '../../types/wallet'
import { PROVIDER_ID, NODE_TOKEN, NODE_SERVER, NODE_PORT } from '../constants'
import algosdk, { Transaction } from 'algosdk'
import BaseWallet from './base'
import type { InitAlgodClient } from './base'
import { TransactionsArray } from 'types/api'

export interface SignerTransaction {
  txn: Transaction
  /**
   * Optional list of addresses that must sign the transactions.
   * Wallet skips to sign this txn if signers is empty array.
   * If undefined, wallet tries to sign it.
   */
  signers?: string[]
}

type InitWallet = {
  id: PROVIDER_ID
  client: PeraWalletConnect
  providers: typeof walletProviders
}

const initAlgodClient: InitAlgodClient = {
  algosdk,
  token: NODE_TOKEN,
  server: NODE_SERVER,
  port: NODE_PORT
}

class PeraWalletClient extends BaseWallet {
  #client: PeraWalletConnect
  id: PROVIDER_ID
  provider: WalletProvider

  constructor(initAlgodClient: InitAlgodClient, initWallet: InitWallet) {
    super(initAlgodClient)

    this.#client = initWallet.client
    this.id = initWallet.id
    this.provider = initWallet.providers[this.id]
  }

  static async init() {
    const PeraWalletConnect = (await import('@perawallet/connect')).PeraWalletConnect
    const peraWallet = new PeraWalletConnect()

    const initWallet: InitWallet = {
      id: PROVIDER_ID.PERA_WALLET,
      client: peraWallet,
      providers: walletProviders
    }

    return new PeraWalletClient(initAlgodClient, initWallet)
  }

  async connect(onDisconnect: () => void): Promise<Wallet> {
    const accounts = await this.#client.connect()
    this.#client.connector?.on('disconnect', onDisconnect)

    if (accounts.length === 0) {
      throw new Error(`No accounts found for ${this.provider}`)
    }

    const mappedAccounts = accounts.map((address: string, index: number) => ({
      name: `Pera Wallet ${index + 1}`,
      address,
      providerId: this.provider.id
    }))

    return {
      ...this.provider,
      accounts: mappedAccounts
    }
  }

  async reconnect(onDisconnect: () => void) {
    const accounts = await this.#client.reconnectSession()
    this.#client.connector?.on('disconnect', onDisconnect)

    if (!accounts) {
      return null
    }

    return {
      ...this.provider,
      accounts: accounts.map((address: string, index: number) => ({
        name: `Pera Wallet ${index + 1}`,
        address,
        providerId: this.provider.id
      }))
    }
  }

  async disconnect() {
    await this.#client.disconnect()
  }

  formatTransactionsArray(transactions: TransactionsArray): SignerTransaction[] {
    const formattedTransactions: SignerTransaction[] = []

    for (const [type, txn] of transactions) {
      if (type === 's') {
        formattedTransactions.push({
          ...this.algosdk.decodeSignedTransaction(new Uint8Array(Buffer.from(txn, 'base64'))),
          signers: []
        })
      } else {
        formattedTransactions.push({
          txn: this.algosdk.decodeUnsignedTransaction(new Uint8Array(Buffer.from(txn, 'base64')))
        })
      }
    }

    return formattedTransactions
  }

  /** @todo implement */
  async signTransactions(_transaction: string[]) {
    return [new Uint8Array()]
  }

  /** @todo get this working on Testnet */
  async signEncodedTransactions(transactions: TransactionsArray) {
    const transactionsToSign = this.formatTransactionsArray(transactions)
    const result = (await this.#client.signTransaction([transactionsToSign])) as Uint8Array[]

    const signedTransactions: Uint8Array[] = []

    let resultIndex = 0

    for (const [type, txn] of transactions) {
      if (type === 'u') {
        signedTransactions.push(result[resultIndex])
        resultIndex++
      } else {
        signedTransactions.push(new Uint8Array(Buffer.from(txn, 'base64')))
      }
    }

    return signedTransactions
  }
}

export default PeraWalletClient.init().catch((e) => {
  if (typeof window !== 'undefined') {
    throw e
  }
})
